'use client';

import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { parse, stringify } from 'qs';
import type { ZodSchema, z } from 'zod';

export type UseQueryParamsReturn<T> = {
	queryParams:      T;
	setQueryParams:   <K extends keyof T>(key: K, value: T[K]) => void;
	clearQueryParams: <K extends keyof T>(key: K) => void;
	resetQueryParams: () => void;
	toQueryString:    (params: Partial<T>) => string;
};

export const useQueryParams = <S extends ZodSchema<unknown>>({
	schema,
	defaultValues,
}: {
	schema:         S;
	defaultValues?: Partial<z.infer<S>>;
}): UseQueryParamsReturn<z.infer<S>> => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const queryString = searchParams.toString();

	const parsedRaw = useMemo(() => {
		return parse(queryString, { ignoreQueryPrefix: true });
	}, [ queryString ]);

	const queryParams = useMemo(() => {
		const result = schema.safeParse({
			...defaultValues,
			...parsedRaw,
		});

		if (!result.success) {
			console.error('Invalid query parameters:', result.error);
			const defaultValueResult = schema.safeParse(defaultValues);

			return defaultValueResult.success ? defaultValueResult.data : schema.parse({});
		}

		return result.data;
	}, [ parsedRaw, defaultValues, schema ]);

	const updateSearchParams = (nextParams: Record<string, unknown>) => {
		const merged = {
			...parsedRaw,
			...nextParams,
		};

		Object.keys(merged).forEach((key) => {
			const val = merged[key];

			if (val === undefined || val === null || val === '') {
				delete merged[key];
			}
		});

		const query = stringify(merged, {
			addQueryPrefix: true,
			arrayFormat:    'repeat',
		});
		router.push(`${pathname}${query}`);
	};

	const setQueryParams = <K extends keyof z.infer<S>>(key: K, value: z.infer<S>[K]) => {
		updateSearchParams({ [key]: value });
	};

	const clearQueryParams = <K extends keyof z.infer<S>>(key: K) => {
		updateSearchParams({ [key]: null });
	};

	const resetQueryParams = () => {
		const cleanDefaults: Record<string, unknown> = {};

		for (const key in defaultValues) {
			const val = defaultValues[key];

			if (val !== undefined && val !== null) {
				cleanDefaults[key] = val;
			}
		}

		const query = stringify(cleanDefaults, {
			addQueryPrefix: true,
			arrayFormat:    'repeat',
		});
		router.push(`${pathname}${query}`);
	};

	const toQueryString = (params: Partial<z.infer<S>>): string => {
		return stringify(params, {
			addQueryPrefix: true,
			arrayFormat:    'repeat',
		});
	};

	return {
		queryParams,
		setQueryParams,
		clearQueryParams,
		resetQueryParams,
		toQueryString,
	};
};
